import { PayloadAction, SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';
import { uuidv4 } from '@lib/core/service/utils';
import { ILoginResponse, actionFacebookLogin, actionGoogleLogin, actionLogin } from '@lib/core/users/slices/auth';
import { uploadUserPictureApiUrlCreator, userDataApiUrlCreator } from '@lib/core/users/slices/urls';
import { TUser } from '@lib/core/users/types';

export interface IUserSlice {
  data: TUser | undefined;
  isLoading: boolean;
  error: SerializedError;
  isFullUserDataLoadedTemporaryHint: boolean;
  isUploadingProfilePicture: boolean;
}

const initialState: IUserSlice = {
  data: undefined,
  error: null,
  isFullUserDataLoadedTemporaryHint: false,
  isLoading: false,
  isUploadingProfilePicture: false,
};

export const actionUploadUserPicture = createTypedAsyncThunk(
  'actionUploadUserPicture',
  async (imagebase64Data: string) =>
    request({
      body: {
        base64: imagebase64Data,
        destination: 'profile_image',
      },
      method: 'PUT',
      url: uploadUserPictureApiUrlCreator(),
    }),
);

export const actionGetUserData = createTypedAsyncThunk(
  'actionGetUserData',
  async () => await request({ url: userDataApiUrlCreator() }),
);

export const actionUpdateUserData = createTypedAsyncThunk(
  'actionUpdateUserData',
  async (requestParams: Partial<TUser>) =>
    request({
      body: requestParams,
      method: REQUEST_METHODS.PATCH,
      url: userDataApiUrlCreator(),
    }),
);

export const userSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionUploadUserPicture.pending, state => {
      state.isUploadingProfilePicture = true;
    });
    builder.addCase(actionUploadUserPicture.fulfilled, state => {
      state.isUploadingProfilePicture = false;
    });
    builder.addCase(actionUploadUserPicture.rejected, state => {
      state.isUploadingProfilePicture = false;
    });
    builder.addMatcher(isAnyOf(actionGetUserData.pending, actionUpdateUserData.pending), state => {
      state.isLoading = true;
    });
    builder.addMatcher(
      isAnyOf(actionGetUserData.fulfilled, actionUpdateUserData.fulfilled),
      (state, action: PayloadAction<TUser>) => {
        // Ignore null user_session_id after registrations. patchQuizComplete associates
        // the anonymous user_session_id to the newly registered user.
        const payload = { ...action.payload };
        const userSessionId = payload.user_session_id || state.data.user_session_id;
        state.data = { ...payload, user_session_id: userSessionId };
        state.isFullUserDataLoadedTemporaryHint = true;
        state.isLoading = false;
      },
    );
    builder.addMatcher(isAnyOf(actionGetUserData.rejected, actionUpdateUserData.rejected), (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
    // Listening for login actions from auth slice, which also returns partially user data
    builder.addMatcher(
      isAnyOf(actionLogin.fulfilled, actionGoogleLogin.fulfilled, actionFacebookLogin.fulfilled),
      (state, action: PayloadAction<ILoginResponse>) => {
        const { payload: { user } = {} } = action;
        state.data = state.data ? { ...state.data, ...user } : user;
      },
    );
  },
  initialState,
  name: 'user',
  reducers: {
    actionGenerateUserSession: state => {
      state.data = { ...state.data, user_session_id: uuidv4() };
    },
    actionResetUserSlice: () => initialState,
  },
});

export default userSlice.reducer;

export const { actionResetUserSlice, actionGenerateUserSession } = userSlice.actions;
